import React, { useCallback, useMemo, useState } from "react";
import { EagerLoaded } from "@common/types/base/orm";
import { toUTCDateOnly } from "@common/helpers/date";
import {
    Invoice,
    InvoiceSource,
    InvoiceStatus as InvoiceStatusEnum,
} from "@common/types/invoice";
import { isInvoiceItemLine } from "@common/helpers/lineItems";
import {
    invoiceHasMatches,
    isDeletableInvoice,
    isDeletableInvoiceSource,
} from "@common/helpers/invoice-status-machine";
import {
    NativeInvoiceCapability,
    canUseNativeInvoiceCapability,
} from "@common/helpers/native-invoice-editability";
import {
    getInvoiceUrl,
    getInvoiceUrlTitle,
    getNativeInvoicePdfFileName,
} from "@helpers/invoice";
import { AccrualDetailsPanel } from "@components/accounting/AccrualDetailsPanel/AccrualDetailsPanel";
import { AccrualMatchesAttachments } from "@components/accounting/AccrualDetailsPanel/AccrualMatchesAttachments";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { useEntitiesById } from "@hooks/useEntities";
import { AccrualClassField } from "@components/accounting/AccrualDetailsPanel/AccrualClassField";
import { useClasses } from "@api/class.api";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useDeleteInvoice } from "@api/invoice.api";
import {
    useDownloadNativeInvoicePdf,
    useDuplicateNativeInvoice,
} from "@api/native-invoice.api";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { useAccountingMigration } from "@api/accountingIntegration.api";
import { useEntityIdsWithAccrualLedger, useLedgers } from "@api/ledger.api";
import { useLockDateState } from "@api/lock.api";
import { getInvoiceOpenAmount } from "@common/helpers/accrual/openAmount";
import { useQuickBooksSettlementExtent } from "./useQuickBooksSettlementExtent";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useScope } from "@state/scopeContext.lib";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useOverlays } from "contexts/OverlayContext";
import { AccrualActivityPanel } from "@components/auditLog/accrual/AccrualActivityPanel";
import { ResourceType } from "@common/types/auditLog";
import { Menu } from "@design-system/Menu/Menu.lib";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { AccountingReportFilters } from "@components/accounting/types";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { ReviseInvoiceModal } from "./ReviseInvoiceModal";
import { useReviseNativeInvoice } from "./NativeInvoiceDrawer/useReviseNativeInvoice";
import { InvoiceStatus } from "./InvoiceStatus";
import { InvoiceStatusSelect } from "./InvoiceStatusSelect";
import { getInvoicePaymentOpenAmount } from "./buildInvoicePaymentAllocations";
import { InvoiceDocumentsCompact } from "./InvoiceDocumentsCompact";
import { KickAccountCard } from "./views/KickAccountCard";
import { QuickbooksAccountCard } from "./views/QuickbooksAccountCard";
import { useCreateInvoiceTemplateFromInvoice } from "@api/invoiceTemplate.api";
import { InvoiceTemplateNameModal } from "./templates/InvoiceTemplateNameModal";
import { createInvoicesUrl } from "./lib/invoicesUrl";
import { useOpenInvoiceSchedule } from "./useOpenInvoiceSchedule";
import { describeInvoiceScheduleTeardown } from "./invoiceScheduleTeardown";
import {
    InvoiceDetailsSchedule,
    InvoiceDetailsScheduleCallbacks,
} from "./InvoiceDetailsSchedule";
import { resolveInvoiceScheduleCellState } from "./views/scheduleCellState";

interface InvoiceStatusChangeParams {
    status: InvoiceStatusEnum;
    uncollectableAt?: string | null;
    voidedAt?: string | null;
    paidAt?: string | null;
}

interface InvoiceDetailsProps {
    invoice: EagerLoaded<Invoice, "lines">;
    setInvoice: (invoice?: Invoice) => void;
    onEdit?: () => void;
    onUpdateMemo?: (memo: string | null) => Promise<void>;
    onUpdateStatus?: (params: InvoiceStatusChangeParams) => Promise<void>;
    onUpdateDueDate?: (due: string | null) => Promise<void>;
    hideCloseButton?: boolean;
    className?: string;
    // Set when rendered inside the invoice line-items drilldown, where opening
    // the same overlay for the same invoice would be a no-op.
    hideShowLineItems?: boolean;
    showLeftBorder?: boolean;
    scheduleCallbacks?: InvoiceDetailsScheduleCallbacks;
}

export const InvoiceDetails: React.FC<InvoiceDetailsProps> = ({
    invoice,
    setInvoice,
    onEdit,
    onUpdateMemo,
    onUpdateStatus,
    onUpdateDueDate,
    hideCloseButton = false,
    className,
    hideShowLineItems = false,
    showLeftBorder,
    scheduleCallbacks,
}) => {
    const { schedules: isSchedulesEnabled } = useTypedFlags();
    const { isActivated: areClassesActivated } = useClasses();
    const isAccrualLedgerEnabled = useIsAccrualLedgerEnabled();
    const { workspaceId } = useScope();
    const { error: toastError, success: toastSuccess } = useToaster();
    const entitiesById = useEntitiesById();
    const entity = entitiesById.get(invoice.entityId);
    // Shared with ReviseInvoiceModal's void-date picker below — revise voids
    // this same invoice/entity, so it needs the same locked-period floor.
    const { lockDate, isResolved: isLockResolved } = useLockDateState(
        invoice.entityId,
    );
    const deleteInvoiceMutation = useDeleteInvoice();
    const downloadNativeInvoicePdf = useDownloadNativeInvoicePdf();
    const duplicateNativeInvoiceMutation = useDuplicateNativeInvoice();
    const createInvoiceTemplate = useCreateInvoiceTemplateFromInvoice();
    const [isSaveAsTemplateOpen, setIsSaveAsTemplateOpen] = useState(false);

    const [showGlDrawer, setShowGlDrawer] = useState(false);
    const { data: ledgers = [] } = useLedgers(
        isAccrualLedgerEnabled ? invoice.entityId : undefined,
    );
    const accrualLedgerId = useMemo(
        () =>
            ledgers.find(
                (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
            )?.id,
        [ledgers],
    );
    const glDrawerFilters = useMemo<AccountingReportFilters>(
        () => ({
            invoiceIds: [invoice.id],
            shouldIncludeMatchedTransactions: true,
            isAllTime: true,
            ledgerId: accrualLedgerId,
        }),
        [invoice.id, accrualLedgerId],
    );
    const invoiceEntityIds = useMemo(
        () => [invoice.entityId],
        [invoice.entityId],
    );
    const { data: accountingMigration } = useAccountingMigration(
        workspaceId ?? null,
        invoice.entityId,
    );
    // Mirror journal-entry generation, which suppresses issuance entries when
    // the invoice's posting date (`dateOnly ?? createdAt`) falls before the
    // bookkeeping start date. Gating on `createdAt` alone would hide the
    // drilldown for a backdated invoice whose `dateOnly` still posts entries
    // (e.g. `createdAt < bookkeepingStartDate <= dateOnly`).
    const isMigratedInvoiceBeforeBookkeepingStart = useMemo(() => {
        if (
            !entity?.bookkeepingStartDate ||
            !accountingMigration?.syncGeneralLedger
        ) {
            return false;
        }
        const invoicePostingDate =
            invoice.dateOnly ?? toUTCDateOnly(invoice.createdAt);
        return invoicePostingDate < toUTCDateOnly(entity.bookkeepingStartDate);
    }, [
        invoice.dateOnly,
        invoice.createdAt,
        entity?.bookkeepingStartDate,
        accountingMigration?.syncGeneralLedger,
    ]);

    // JEs are only generated for accrual-ledger invoices past DRAFT status.
    // Migrated invoices before the bookkeeping start date never post entries,
    // so don't offer the menu option for them at all.
    const canOpenGlDrilldown =
        isAccrualLedgerEnabled &&
        invoice.status !== InvoiceStatusEnum.DRAFT &&
        !!accrualLedgerId &&
        !isMigratedInvoiceBeforeBookkeepingStart;

    const invoiceScheduleState = useMemo(
        () => resolveInvoiceScheduleCellState(invoice),
        [invoice],
    );
    const onViewRecognitionSchedule =
        useOpenInvoiceSchedule(invoiceScheduleState);
    const canViewRecognitionSchedule =
        isSchedulesEnabled && onViewRecognitionSchedule != null;

    const handleDelete = useCallback(async () => {
        if (!workspaceId) {
            return;
        }
        await deleteInvoiceMutation.mutateAsync({
            workspaceId,
            invoiceId: invoice.id,
        });
        setInvoice(undefined);
    }, [workspaceId, invoice.id, deleteInvoiceMutation, setInvoice]);

    // Voiding an invoice cannot be undone, so transitions into VOID from the
    // status select are held here and require explicit confirmation before
    // commit. Re-selecting VOID on an already voided invoice skips the gate —
    // that is not a new irreversible action.
    const [pendingVoidChange, setPendingVoidChange] =
        useState<InvoiceStatusChangeParams | null>(null);

    const handleUpdateStatus = useCallback(
        async (params: InvoiceStatusChangeParams) => {
            if (!onUpdateStatus) {
                return;
            }
            if (
                params.status === InvoiceStatusEnum.VOID &&
                invoice.status !== InvoiceStatusEnum.VOID
            ) {
                setPendingVoidChange(params);
                return;
            }
            await onUpdateStatus(params);
        },
        [onUpdateStatus, invoice.status],
    );

    const handleConfirmVoid = useCallback(async () => {
        if (!onUpdateStatus || !pendingVoidChange) {
            return;
        }
        try {
            await onUpdateStatus(pendingVoidChange);
            setPendingVoidChange(null);
        } catch {
            // Keep the modal open so the user can retry; the mutation's onError
            // already surfaces the failure and keeps the cache consistent.
        }
    }, [onUpdateStatus, pendingVoidChange]);

    const handleCancelVoid = useCallback(() => {
        setPendingVoidChange(null);
    }, []);

    const copyLinkToClipboard = useCopyToClipboard({
        successMessage: "Link copied to clipboard",
    });

    const invoiceDeepLinkUrl = `${window.location.origin}${createInvoicesUrl({ invoiceIds: [invoice.id] })}`;

    const handleCopyUrl = useCallback(() => {
        copyLinkToClipboard(invoiceDeepLinkUrl);
    }, [invoiceDeepLinkUrl, copyLinkToClipboard]);

    const { openOverlay } = useOverlays();

    const handleEditNativeInvoice = useCallback(() => {
        openOverlay("nativeInvoiceDrawer", { invoiceId: invoice.id });
    }, [invoice.id, openOverlay]);

    const handleShowLineItems = useCallback(() => {
        openOverlay("invoiceLineItemsDrilldown", {
            invoiceId: invoice.id,
            entityIds: invoiceEntityIds,
        });
    }, [invoice.id, invoiceEntityIds, openOverlay]);

    // Duplicating is available for a Kick-native invoice in any status: it
    // creates an independent Draft copy, unlike Revise (which links back to
    // an Open original and is only offered there).
    const handleDuplicate = useCallback(async () => {
        if (!workspaceId || duplicateNativeInvoiceMutation.isPending) {
            return;
        }
        try {
            const result = await duplicateNativeInvoiceMutation.mutateAsync({
                workspaceId,
                entityId: invoice.entityId,
                invoiceId: invoice.id,
            });
            openOverlay("nativeInvoiceDrawer", { invoiceId: result.data.id });
        } catch {
            toastError("Couldn't duplicate the invoice. Try again.");
        }
    }, [
        workspaceId,
        invoice.entityId,
        invoice.id,
        duplicateNativeInvoiceMutation,
        openOverlay,
        toastError,
    ]);

    const isNativeInvoice = invoice.source === InvoiceSource.MANUAL;
    const canCopyPaymentLink =
        isNativeInvoice &&
        invoice.collectPayment &&
        Boolean(invoice.paymentToken);

    const handleCopyPaymentLink = useCallback(() => {
        if (!invoice.paymentToken) {
            return;
        }
        copyLinkToClipboard(
            `${window.location.origin}/pay/${invoice.paymentToken}`,
        );
    }, [copyLinkToClipboard, invoice.paymentToken]);

    // One shared capability matrix governs which editors this invoice exposes.
    // Non-native invoices bypass the policy (nativeCan is always false), keeping
    // their prior behavior.
    const nativeCan = useCallback(
        (capability: NativeInvoiceCapability) =>
            isNativeInvoice &&
            canUseNativeInvoiceCapability({
                status: invoice.status,
                capability,
            }),
        [isNativeInvoice, invoice.status],
    );

    // View opens the drawer for any Kick invoice. Drafts get the full editor;
    // other statuses open as a PDF preview. Open content is revised (menu).
    const canReviseNative = nativeCan(NativeInvoiceCapability.REVISE);

    const [showReviseModal, setShowReviseModal] = useState(false);
    const revise = useReviseNativeInvoice({
        invoice,
        entity,
        enabled: showReviseModal,
        onRevised: () => setShowReviseModal(false),
    });
    // Native (Kick-created) invoices have no external counterparty system,
    // so the external status is not meaningful and is hidden from the detail view.
    const externalStatusBadge =
        !isNativeInvoice && invoice.externalStatus ? (
            <InvoiceStatus status={invoice.externalStatus} showText />
        ) : undefined;
    const statusBadge = <InvoiceStatus status={invoice.status} showText />;
    // Kick-native invoice status (draft/open/paid/void/uncollectable) is a core
    // invoicing capability available on every plan, independent of the
    // Accrual Ledger (Plus) capability that gates GL/journal-entry posting.
    // Non-native invoices (synced from Stripe/Mercury/etc.) keep the status
    // field limited to accrual-ledger workspaces, matching prior behavior.
    const showStatusField = isAccrualLedgerEnabled || isNativeInvoice;

    const getAccountNode = (): React.ReactNode => {
        if (isNativeInvoice) {
            return <KickAccountCard />;
        }
        if (invoice.financialAccount) {
            return (
                <AccountCard
                    account={invoice.financialAccount}
                    onlyNumber
                    showType={false}
                    iconSize="xs"
                />
            );
        }
        if (invoice.source === InvoiceSource.QUICKBOOKS) {
            return <QuickbooksAccountCard />;
        }
        return undefined;
    };

    const accountNode = getAccountNode();

    // Only kind: "item" rows are independent AR/match exposure — kind:
    // "tax"/"discount" rows are synthetic header-tax/discount splits (see
    // InvoiceLineItemKind) with no matchedAmount or class coding of their own.
    const itemLines = useMemo(
        () => invoice.lines?.filter(isInvoiceItemLine),
        [invoice.lines],
    );

    const hasMatches = useMemo(() => invoiceHasMatches(itemLines), [itemLines]);
    const quickBooksSettlement = useQuickBooksSettlementExtent(invoice);

    // Open balance drives the payment amount, its validation, and the modal
    // gate. Reuse the shared helper (invoice.total - matched) so taxed or
    // discounted invoices don't diverge from the synthetic tax/discount split
    // rows that itemLines excludes.
    const accrualEntityIds = useEntityIdsWithAccrualLedger(invoiceEntityIds);

    const openAmount = useMemo(
        () => getInvoiceOpenAmount(invoice, accrualEntityIds),
        [invoice, accrualEntityIds],
    );

    const scheduleTeardown = useMemo(
        () => describeInvoiceScheduleTeardown({ invoice, openAmount }),
        [invoice, openAmount],
    );

    const paymentOpenAmount = useMemo(
        () =>
            getInvoicePaymentOpenAmount({
                lines: invoice.lines ?? [],
                openAmount,
            }),
        [invoice.lines, openAmount],
    );

    const handlePaymentRecorded = useCallback(() => {
        setInvoice({
            ...invoice,
            status: InvoiceStatusEnum.PAID,
            paidAt: new Date(),
        });
    }, [invoice, setInvoice]);

    const recordPayment = useMemo(
        () => ({
            invoiceId: invoice.id,
            entityId: invoice.entityId,
            openAmount: paymentOpenAmount,
            lines: invoice.lines,
            counterpartyId: invoice.counterparty?.id,
            onRecorded: handlePaymentRecorded,
        }),
        [
            invoice.id,
            invoice.entityId,
            invoice.counterparty?.id,
            invoice.lines,
            paymentOpenAmount,
            handlePaymentRecorded,
        ],
    );

    const selectedClassIds = useMemo(() => {
        const classIds = new Set<string>();
        (itemLines ?? []).forEach((line) => {
            line.classAssignments?.forEach((assignment) => {
                classIds.add(assignment.classId);
            });
        });
        return Array.from(classIds);
    }, [itemLines]);

    const classNode = areClassesActivated ? (
        <AccrualClassField classIds={selectedClassIds} />
    ) : undefined;

    const handleDownloadPdf = useCallback(async () => {
        if (!workspaceId) {
            return;
        }
        try {
            await downloadNativeInvoicePdf.mutateAsync({
                workspaceId,
                entityId: invoice.entityId,
                invoiceId: invoice.id,
                fileName: getNativeInvoicePdfFileName(invoice),
            });
        } catch {
            toastError("Failed to download PDF");
        }
    }, [workspaceId, invoice, downloadNativeInvoicePdf, toastError]);

    const showNativeInvoiceActions = invoice.source === InvoiceSource.MANUAL;

    const isDeletable = isDeletableInvoice(invoice);
    const deleteDisabledTooltip = !isDeletableInvoiceSource(invoice.source)
        ? "Synced invoices can't be deleted"
        : "Only draft invoices can be deleted";
    const canEditNativeStatus =
        !isNativeInvoice || nativeCan(NativeInvoiceCapability.STATUS);
    const canEditNativeDueDate = nativeCan(NativeInvoiceCapability.DUE_DATE);
    const isDocumentsReadOnly =
        isNativeInvoice && !nativeCan(NativeInvoiceCapability.DOCUMENTS);
    const isImporterEditable = invoice.source === InvoiceSource.IMPORTER;

    return (
        <>
            <AccrualDetailsPanel
                statusBadge={externalStatusBadge ?? statusBadge}
                showLeftBorder={showLeftBorder}
                metadata={{
                    externalStatusText: externalStatusBadge,
                    statusText: showStatusField ? statusBadge : undefined,
                    statusSelect:
                        showStatusField &&
                        onUpdateStatus &&
                        canEditNativeStatus ? (
                            <InvoiceStatusSelect
                                value={invoice.status}
                                invoiceNumber={invoice.invoiceNumber}
                                uncollectableAt={invoice.uncollectableAt}
                                voidedAt={invoice.voidedAt}
                                openAmount={openAmount}
                                currency={invoice.currency}
                                scheduleTeardown={scheduleTeardown}
                                invoiceDate={
                                    isNativeInvoice
                                        ? invoice.invoiceDate
                                        : undefined
                                }
                                entityId={invoice.entityId}
                                onValueChange={handleUpdateStatus}
                                hasMatches={hasMatches}
                                quickBooksSettlement={quickBooksSettlement}
                                hasCounterparty={invoice.counterparty != null}
                                recordPayment={recordPayment}
                            />
                        ) : undefined,
                    // Native invoices have no external counterparty system,
                    // so there's no "external status" to disambiguate from —
                    // just call this "Status" rather than "Kick status".
                    statusLabel: isNativeInvoice ? "Status" : undefined,
                    counterpartyName: invoice.counterparty?.name,
                    counterpartyEmail: invoice.counterparty?.email,
                    accountNode,
                    createdAt: invoice.dateOnly ?? invoice.createdAt,
                    dueDate: invoice.dueDateOnly ?? invoice.due,
                    onUpdateDueDate: canEditNativeDueDate
                        ? onUpdateDueDate
                        : undefined,
                    invoiceDate: isNativeInvoice
                        ? invoice.invoiceDate
                        : undefined,
                    uncollectableAt: invoice.uncollectableAt,
                    voidedAt: invoice.voidedAt,
                    entity,
                    classNode,
                    recognitionScheduleNode:
                        isSchedulesEnabled && scheduleCallbacks ? (
                            <InvoiceDetailsSchedule
                                invoice={invoice}
                                {...scheduleCallbacks}
                            />
                        ) : undefined,
                    documentSection: (
                        <InvoiceDocumentsCompact
                            invoiceId={invoice.id}
                            financialDocumentIds={invoice.financialDocumentIds}
                            isReadOnly={isDocumentsReadOnly}
                            pdfAttachment={
                                showNativeInvoiceActions
                                    ? {
                                          fileName:
                                              getNativeInvoicePdfFileName(
                                                  invoice,
                                              ),
                                          onOpen: handleDownloadPdf,
                                      }
                                    : undefined
                            }
                        />
                    ),
                    memo: invoice.memo,
                    memoLabel: "Memo",
                    onUpdateMemo,
                    onCopyUrl: handleCopyUrl,
                    urlLabel: "URL",
                }}
                amount={invoice.total}
                externalUrl={getInvoiceUrl(invoice)}
                externalUrlTitle={getInvoiceUrlTitle(invoice)}
                matchesPlacement="details"
                matchedTransactionsSection={
                    <AccrualMatchesAttachments
                        documentType="invoice"
                        documentId={invoice.id}
                        source={invoice.source}
                    />
                }
                activitySection={
                    <AccrualActivityPanel
                        resourceType={ResourceType.INVOICE}
                        resourceId={invoice.id}
                        entityId={invoice.entityId}
                    />
                }
                onEdit={isImporterEditable ? onEdit : undefined}
                onView={isNativeInvoice ? handleEditNativeInvoice : undefined}
                onDelete={handleDelete}
                deleteEntityName="invoice"
                deleteDisabled={!isDeletable}
                deleteDisabledTooltip={deleteDisabledTooltip}
                onClose={() => setInvoice(undefined)}
                hideCloseButton={hideCloseButton}
                className={className}
                additionalMenuItems={
                    !hideShowLineItems ||
                    isNativeInvoice ||
                    canReviseNative ||
                    canOpenGlDrilldown ||
                    canViewRecognitionSchedule ||
                    canCopyPaymentLink ? (
                        <>
                            {!hideShowLineItems && (
                                <Menu.Item
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleShowLineItems();
                                    }}
                                >
                                    Show line items
                                </Menu.Item>
                            )}
                            {isNativeInvoice && (
                                <Menu.Item
                                    disabled={
                                        duplicateNativeInvoiceMutation.isPending
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void handleDuplicate();
                                    }}
                                >
                                    Duplicate invoice
                                </Menu.Item>
                            )}
                            {isNativeInvoice && (
                                <Menu.Item
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsSaveAsTemplateOpen(true);
                                    }}
                                >
                                    Save as template
                                </Menu.Item>
                            )}
                            {canCopyPaymentLink && (
                                <Menu.Item
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void handleCopyPaymentLink();
                                    }}
                                >
                                    Copy payment link
                                </Menu.Item>
                            )}
                            {canReviseNative && (
                                <Menu.Item
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowReviseModal(true);
                                    }}
                                >
                                    Revise invoice
                                </Menu.Item>
                            )}
                            {canOpenGlDrilldown && (
                                <Menu.Item
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowGlDrawer(true);
                                    }}
                                >
                                    View journal entries
                                </Menu.Item>
                            )}
                            {canViewRecognitionSchedule && (
                                <Menu.Item
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onViewRecognitionSchedule();
                                    }}
                                >
                                    View recognition schedule
                                </Menu.Item>
                            )}
                        </>
                    ) : undefined
                }
            />
            {canOpenGlDrilldown && (
                <GlDrilldownDrawer
                    open={showGlDrawer}
                    onClose={() => setShowGlDrawer(false)}
                    filters={glDrawerFilters}
                    entityIds={invoiceEntityIds}
                />
            )}
            <ConfirmationModal
                show={pendingVoidChange !== null}
                onHide={handleCancelVoid}
                onReject={handleCancelVoid}
                onConfirm={handleConfirmVoid}
                title="Void this invoice?"
                question="Voiding an invoice cannot be undone. Are you sure you want to continue?"
                yes="Void invoice"
                yesBtnVariant="destructive"
                no="Cancel"
            />
            <ReviseInvoiceModal
                show={showReviseModal}
                invoiceNumber={invoice.invoiceNumber ?? ""}
                invoiceDate={invoice.invoiceDate}
                lockDate={lockDate}
                isLockResolved={isLockResolved}
                isProcessing={revise.isProcessing}
                isLoading={revise.isLoading}
                onCancel={() => setShowReviseModal(false)}
                onConfirm={(voidDate) => {
                    void revise.revise(voidDate);
                }}
            />
            {isNativeInvoice && (
                <InvoiceTemplateNameModal
                    show={isSaveAsTemplateOpen}
                    title="Save as template"
                    submitLabel="Save"
                    isSubmitting={createInvoiceTemplate.isPending}
                    onSubmit={async (name) => {
                        await createInvoiceTemplate.mutateAsync({
                            entityId: invoice.entityId,
                            invoiceId: invoice.id,
                            name,
                            suppressErrorToast: true,
                        });
                        toastSuccess("Template saved");
                    }}
                    onHide={() => setIsSaveAsTemplateOpen(false)}
                />
            )}
        </>
    );
};
